<script setup lang="ts">
import { AwSkeleton } from "@asap/design";
import type { RetentionRecommendation, RetentionRow } from "./types";

const SKELETON_ROW_COUNT = 3;
const SKELETON_RECOMMENDATION_COUNT = 2;

withDefaults(
  defineProps<{
    loading?: boolean;
    title?: string;
    subtitle?: string;
    rows?: RetentionRow[];
    recommendations?: RetentionRecommendation[];
  }>(),
  {
    loading: false,
    title: "Fidélisation",
    rows: undefined,
    recommendations: undefined,
    subtitle: "Rétention et réactivation du vivier",
  }
);

const ui = {
  rows: "flex flex-col gap-2.5",
  row: "flex flex-col gap-1",
  rowHeader: "flex items-baseline justify-between gap-2",
  rowLabel: "text-sm text-secondary-700",
  rowValueGroup: "flex flex-wrap items-baseline justify-end gap-1.5",
  rowValue: "text-base font-semibold text-secondary-900",
  rowDelta: "text-xs font-medium text-secondary-700",
  rowComparison: "text-xs text-secondary-700",
  recommendations: "flex flex-col gap-1 rounded-lg bg-secondary-50 p-2",
  recommendation: "flex items-center gap-2",
  recommendationBadge:
    "flex size-8 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-sm font-semibold text-secondary-900",
  recommendationCheck: "i-ph-check-bold text-base text-green-600",
  recommendationBadgeSuccess: "bg-green-100",
  recommendationText: "text-xs leading-tight text-secondary-700",
  skeletonLabel: "h-3.5 w-36",
  skeletonValue: "h-4 w-10",
  skeletonBar: "h-0.5 w-full rounded-full",
  skeletonBadge: "size-8 shrink-0 rounded-lg",
  skeletonText: "h-3 w-full",
};
</script>

<template>
  <ConsultantKpiCard :title="title" :subtitle="subtitle" bordered>
    <div :class="ui.rows">
      <template v-if="loading">
        <div v-for="index in SKELETON_ROW_COUNT" :key="`retention-skeleton-${index}`" :class="ui.row">
          <div :class="ui.rowHeader">
            <AwSkeleton :class="ui.skeletonLabel" />
            <AwSkeleton :class="ui.skeletonValue" />
          </div>
          <AwSkeleton :class="ui.skeletonBar" />
        </div>
      </template>
      <template v-else>
        <div v-for="row in rows" :key="row.label" :class="ui.row">
          <div :class="ui.rowHeader">
            <span :class="ui.rowLabel">{{ row.label }}</span>
            <div :class="ui.rowValueGroup">
              <span :class="ui.rowValue">{{ row.value }}</span>
              <span v-if="row.delta" :class="ui.rowDelta">{{ row.delta }}</span>
              <span v-if="row.comparison" :class="ui.rowComparison">{{ row.comparison }}</span>
            </div>
          </div>
          <ConsultantKpiProgressBar
            v-if="!row.unavailable && !row.missing"
            :percent="row.progressPercent"
            tone="secondary"
            :leading-label="`${row.progressPercent}%`"
            :trailing-label="row.target"
          />
        </div>
      </template>
    </div>

    <div :class="ui.recommendations">
      <template v-if="loading">
        <div
          v-for="index in SKELETON_RECOMMENDATION_COUNT"
          :key="`recommendation-skeleton-${index}`"
          :class="ui.recommendation"
        >
          <AwSkeleton :class="ui.skeletonBadge" />
          <AwSkeleton :class="ui.skeletonText" />
        </div>
      </template>
      <template v-else>
        <div v-for="recommendation in recommendations" :key="recommendation.text" :class="ui.recommendation">
          <span
            :class="[
              ui.recommendationBadge,
              recommendation.status === 'success' ? ui.recommendationBadgeSuccess : undefined,
            ]"
          >
            <span v-if="recommendation.status === 'success'" :class="ui.recommendationCheck"></span>
            <template v-else>{{ recommendation.badge }}</template>
          </span>
          <span :class="ui.recommendationText">{{ recommendation.text }}</span>
        </div>
      </template>
    </div>
  </ConsultantKpiCard>
</template>
